/**
 * Retry Engine - Core Retry Execution Logic
 *
 * This module provides the core retry execution engine with integrated metrics,
 * backoff calculation, and circuit breaker coordination. It handles the actual
 * retry attempts and manages the retry lifecycle.
 */

import { CanceledError, isCancel } from 'axios';

import { getObservability } from '../../../di/container';
import {
  RequestStatus,
  DEFAULT_RETRY_CONFIG,
  CoreNotificationType,
} from '../../../types/api';
import { ObservabilitySeverity } from '../../../types/observability';
import { dayjs } from '../../../utils/date';
import { calculateDuration } from '../core/request.tracker';

import type {
  RequestContext,
  RequestConfig,
  RetryConfig,
  RetryAttempt,
  PerformanceMetrics,
  RetryCoordinationResult,
  AxiosInstanceWithOriginal,
} from '../../../types/api';
import type { CoreNotificationEvent } from '../../../types/api/notification.types';
import type {
  RetryEngine,
  RetryMetricsSnapshot,
} from '../../../types/api/retry.types';
import type { AxiosInstance, GenericAbortSignal } from 'axios';

// =============================================================================
// RETRY ENGINE IMPLEMENTATION
// =============================================================================

export class RetryEngineImpl implements RetryEngine {
  private config: RetryConfig;
  private metrics: Map<string, RetryAttempt[]> = new Map();
  private globalMetrics: {
    totalAttempts: number;
    successfulRetries: number;
    failedRetries: number;
    totalDelay: number;
    lastRetryTimestamp?: number;
  } = {
    totalAttempts: 0,
    successfulRetries: 0,
    failedRetries: 0,
    totalDelay: 0,
  };
  private notificationHandlers: Array<(event: CoreNotificationEvent) => void> =
    [];
  private observability = getObservability();

  constructor(config: RetryConfig) {
    this.config = config;
  }

  /**
   * Executes retry logic for a failed request
   */
  async executeRetry(
    context: RequestContext,
    axiosClient: AxiosInstance
  ): Promise<RetryCoordinationResult> {
    const startTime = dayjs().valueOf();

    // Use originalRequest to bypass interceptors and prevent infinite recursion
    // When retry requests go through interceptors, they re-enter the coordinator,
    // causing a circular loop. Using the original unwrapped request method avoids this.
    const clientWithOriginal = axiosClient as AxiosInstanceWithOriginal;
    const requestFn =
      clientWithOriginal.originalRequest ||
      axiosClient.request.bind(axiosClient);

    // Check if we should continue retrying
    if (!this.shouldContinueRetrying(context)) {
      return {
        success: false,
        error: context.lastError || new Error('Retry attempts exhausted'),
        context,
        metrics: this.createMetrics(context.id, startTime),
      };
    }

    const nextAttempt = context.retryCount + 1;

    // The classification's delay was worked out for the first retry only (its
    // retryDelay is dropped before recursing), so every later attempt backs off
    // from its own retry count.
    const delay = context.classification?.retryDelay
      ? context.classification.retryDelay
      : this.calculateDelay(context.retryCount, this.config);

    // Create retry attempt record
    const retryAttempt: RetryAttempt = {
      attemptNumber: nextAttempt,
      delay,
      error: context.lastError || new Error('Unknown error'),
      timestamp: dayjs().valueOf(),
      requestId: context.id,
    };

    // Update metrics
    this.updateMetrics(retryAttempt);

    // Notify retry start
    this.notifyEvent({
      type: CoreNotificationType.RETRY_ATTEMPT,
      requestId: context.id,
      message: `Retry attempt ${nextAttempt}/${context.maxRetries}`,
      timestamp: dayjs().valueOf(),
      data: {
        attempt: nextAttempt,
        delay,
        error: retryAttempt.error.message,
      },
    });

    // Wait for calculated delay. An abort ends the wait, and the retries, at once.
    try {
      await this.delay(delay, context.config.signal);
    } catch (error) {
      return this.createCancelledResult(context, error as Error, startTime);
    }

    // Prepare retry config
    const retryConfig = this.createRetryConfig(context, nextAttempt);

    try {
      // Execute the retry request using the original unwrapped request method
      const response = await requestFn(retryConfig);

      // Success! Update metrics
      this.recordSuccessfulRetry();

      // Notify success
      this.notifyEvent({
        type: CoreNotificationType.RETRY_SUCCESS,
        requestId: context.id,
        message: `Retry succeeded on attempt ${nextAttempt}`,
        timestamp: dayjs().valueOf(),
        data: { attempt: nextAttempt },
      });

      return {
        success: true,
        response,
        context: {
          ...context,
          retryCount: nextAttempt,
          status: RequestStatus.COMPLETED,
          response,
        },
        metrics: this.createMetrics(context.id, startTime, dayjs().valueOf()),
      };
    } catch (error) {
      // Retry failed, record the failure
      this.recordFailedRetry();

      // Update context for next iteration
      const updatedContext = {
        ...context,
        retryCount: nextAttempt,
        lastError: error as Error,
        status: RequestStatus.RETRYING,
        classification: context.classification && {
          ...context.classification,
          retryDelay: undefined,
        },
      };

      // A request aborted mid-flight is not retried.
      if (isCancel(error)) {
        return this.createCancelledResult(
          updatedContext,
          error as Error,
          startTime
        );
      }

      // If we have more retries available, recurse
      if (this.shouldContinueRetrying(updatedContext)) {
        return await this.executeRetry(updatedContext, axiosClient);
      }

      // No more retries. The failure itself is reported once, by the request
      // coordinator's toTypedError (reportRequestFailure); a report here too
      // would send the same failure twice. The breadcrumb puts the retries on
      // that report's trail.
      this.observability.addBreadcrumb(
        'All retry attempts exhausted',
        'api.retry',
        ObservabilitySeverity.WARNING,
        {
          url: context.config.url,
          totalAttempts: updatedContext.retryCount,
          errorMessage: (error as Error).message,
        }
      );

      this.observability.recordMetric('retry.exhausted', 1, 'count', {
        endpoint: context.config.url || 'unknown',
        attempts: String(updatedContext.retryCount),
      });

      // Notify exhaustion
      this.notifyEvent({
        type: CoreNotificationType.RETRY_EXHAUSTED,
        requestId: context.id,
        message: `Retry attempts exhausted (${updatedContext.retryCount}/${context.maxRetries})`,
        timestamp: dayjs().valueOf(),
        data: {
          finalAttempt: updatedContext.retryCount,
          error: (error as Error).message,
        },
      });

      return {
        success: false,
        error: error as Error,
        context: {
          ...updatedContext,
          status: RequestStatus.FAILED,
        },
        metrics: this.createMetrics(context.id, startTime, dayjs().valueOf()),
      };
    }
  }

  /**
   * Calculates retry delay using exponential backoff with jitter
   */
  calculateDelay(attempt: number, config: RetryConfig): number {
    const baseDelay = config.baseDelay;
    const exponentialDelay =
      baseDelay * Math.pow(config.exponentialBase, attempt);

    // Apply maximum delay limit
    const cappedDelay = Math.min(exponentialDelay, config.maxDelay);

    // Apply jitter to prevent thundering herd
    const jitterRange = cappedDelay * config.jitterFactor;
    const jitter = (Math.random() * 2 - 1) * jitterRange;

    const finalDelay = Math.max(0, cappedDelay + jitter);

    return Math.round(finalDelay);
  }

  /**
   * Updates retry metrics with attempt data
   */
  updateMetrics(attempt: RetryAttempt): void {
    // Add to per-request metrics
    const requestMetrics = this.metrics.get(attempt.requestId) || [];
    requestMetrics.push(attempt);
    this.metrics.set(attempt.requestId, requestMetrics);

    // Update global metrics
    this.globalMetrics.totalAttempts++;
    this.globalMetrics.totalDelay += attempt.delay;
    this.globalMetrics.lastRetryTimestamp = attempt.timestamp;
  }

  /**
   * Determines if retrying should continue
   */
  shouldContinueRetrying(context: RequestContext): boolean {
    // Check if we've exceeded max attempts
    if (context.retryCount >= context.maxRetries) {
      return false;
    }

    // If we have classification data, use it for intelligent retry decisions
    if (context.classification) {
      return context.classification.shouldRetry;
    }

    // Fallback to basic checks if no classification available
    if (context.lastError) {
      const error = context.lastError as { response?: { status?: number } };

      // Don't retry client errors (4xx) - these are user errors
      if (
        error.response?.status &&
        error.response.status >= 400 &&
        error.response.status < 500
      ) {
        return false;
      }

      // Don't retry auth errors (handled separately)
      if (error.response?.status === 401) {
        return false;
      }
    }

    return true;
  }

  /**
   * Gets current retry metrics snapshot
   */
  getRetryMetrics(): RetryMetricsSnapshot {
    const totalAttempts = this.globalMetrics.totalAttempts;
    const successRate =
      totalAttempts > 0
        ? (this.globalMetrics.successfulRetries / totalAttempts) * 100
        : 0;

    const averageDelay =
      totalAttempts > 0 ? this.globalMetrics.totalDelay / totalAttempts : 0;

    return {
      totalAttempts,
      successfulRetries: this.globalMetrics.successfulRetries,
      failedRetries: this.globalMetrics.failedRetries,
      averageDelay,
      totalDelay: this.globalMetrics.totalDelay,
      retrySuccessRate: successRate,
      lastRetryTimestamp: this.globalMetrics.lastRetryTimestamp,
    };
  }

  /**
   * Resets all retry metrics
   */
  resetMetrics(): void {
    this.metrics.clear();
    this.globalMetrics = {
      totalAttempts: 0,
      successfulRetries: 0,
      failedRetries: 0,
      totalDelay: 0,
    };
  }

  /**
   * Gets retry attempts for a specific request
   */
  getRequestRetryAttempts(requestId: string): RetryAttempt[] {
    return this.metrics.get(requestId) || [];
  }

  /**
   * Adds a notification handler
   */
  addNotificationHandler(
    handler: (event: CoreNotificationEvent) => void
  ): void {
    this.notificationHandlers.push(handler);
  }

  /**
   * Removes a notification handler
   */
  removeNotificationHandler(
    handler: (event: CoreNotificationEvent) => void
  ): void {
    const index = this.notificationHandlers.indexOf(handler);
    if (index > -1) {
      this.notificationHandlers.splice(index, 1);
    }
  }

  /**
   * Updates retry configuration
   */
  updateConfig(config: Partial<RetryConfig>): void {
    this.config = { ...this.config, ...config };
  }

  // =============================================================================
  // PRIVATE HELPER METHODS
  // =============================================================================

  private async delay(ms: number, signal?: GenericAbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new CanceledError());
        return;
      }

      const onAbort = (): void => {
        clearTimeout(timer);
        reject(new CanceledError());
      };
      const timer = setTimeout(() => {
        signal?.removeEventListener?.('abort', onAbort);
        resolve();
      }, ms);
      signal?.addEventListener?.('abort', onAbort);
    });
  }

  private createCancelledResult(
    context: RequestContext,
    error: Error,
    startTime: number
  ): RetryCoordinationResult {
    return {
      success: false,
      error,
      context: { ...context, status: RequestStatus.CANCELLED },
      metrics: this.createMetrics(context.id, startTime, dayjs().valueOf()),
    };
  }

  private createRetryConfig(
    context: RequestContext,
    attempt: number
  ): RequestConfig {
    return {
      ...context.config,
      metadata: {
        ...context.config.metadata,
        requestId: context.id,
        timestamp: dayjs().valueOf(),
        retryCount: attempt,
        isRetry: true,
        parentRequestId: context.id,
        skipDeduplication: true,
      },
    };
  }

  private recordSuccessfulRetry(): void {
    this.globalMetrics.successfulRetries++;
  }

  private recordFailedRetry(): void {
    this.globalMetrics.failedRetries++;
  }

  private createMetrics(
    requestId: string,
    startTime: number,
    endTime?: number
  ): PerformanceMetrics {
    const now = endTime || dayjs().valueOf();
    const attempts = this.getRequestRetryAttempts(requestId);

    return {
      requestId,
      startTime,
      endTime: now,
      duration: calculateDuration(startTime, now),
      retryCount: attempts.length,
      totalDuration: calculateDuration(startTime, now),
    };
  }

  private notifyEvent(event: CoreNotificationEvent): void {
    this.notificationHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        console.warn('Notification handler error:', error);
      }
    });
  }
}

// =============================================================================
// FACTORY FUNCTIONS
// =============================================================================

/**
 * Creates a new retry engine instance
 */
export function createRetryEngine(config: RetryConfig): RetryEngine {
  return new RetryEngineImpl(config);
}

/**
 * Creates a retry engine with default configuration
 */
export function createDefaultRetryEngine(): RetryEngine {
  return new RetryEngineImpl(DEFAULT_RETRY_CONFIG);
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Calculates exponential backoff delay
 */
export function calculateExponentialBackoff(
  attempt: number,
  baseDelay: number = 1000,
  exponentialBase: number = 2,
  maxDelay: number = 10000,
  jitterFactor: number = 0.1
): number {
  const engine = createRetryEngine({
    ...DEFAULT_RETRY_CONFIG,
    baseDelay,
    maxDelay,
    exponentialBase,
    jitterFactor,
  });

  return engine.calculateDelay(attempt, {
    ...DEFAULT_RETRY_CONFIG,
    baseDelay,
    maxDelay,
    exponentialBase,
    jitterFactor,
  });
}

/**
 * Validates retry configuration
 */
export function validateRetryConfig(config: RetryConfig): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (config.maxAttempts < 0) {
    errors.push('maxAttempts must be >= 0');
  }

  if (config.baseDelay < 0) {
    errors.push('baseDelay must be >= 0');
  }

  if (config.maxDelay < config.baseDelay) {
    errors.push('maxDelay must be >= baseDelay');
  }

  if (config.exponentialBase <= 0) {
    errors.push('exponentialBase must be > 0');
  }

  if (config.jitterFactor < 0 || config.jitterFactor > 1) {
    errors.push('jitterFactor must be between 0 and 1');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
